import { Controller } from "@hotwired/stimulus"

// § 3.5 Tier 4 Step 42 — Action-Item pin overlay for the Forge Viewer.
//
// Mounted on the same root element as `forge-viewer` so it can read
// the viewer instance the sibling controller exposes via
// `element._forgeViewer` (set by forge_viewer_controller after the
// viewer mounts). The overlay:
//
//   1. Fetches `/projects/:id/bim/:id/pins.json` once geometry is
//      loaded.
//   2. Resolves each pin's externalIds → dbIds via the model's
//      `getExternalIdMapping` cache. Pins whose primary externalId no
//      longer maps surface as gray "orphan" sprites with a tooltip
//      ("Element no longer in model"); the action-item card surfaces
//      its own banner via `data-orphan="1"` (Step 45).
//   3. Anchors a THREE.Sprite at the pin's persisted world-space
//      position. Re-anchors on `viewer.loadDocumentNode` (view swap)
//      because the dbId table changes.
//   4. Re-clusters at screen-space density on CAMERA_CHANGE_EVENT,
//      throttled to 250 ms.
//   5. Click → opens the existing Punchlist slide-over via the page's
//      action-items controller (we delegate via a custom event the
//      slide-over listens for) AND restores the captured viewpoint
//      via the viewer's `restoreState` path.
//
// Architecture Decision #3 lives here in spirit: persisted
// externalIds, runtime dbIds, never the other way around.
const PIN_FETCH_RETRY_MS = 750
const CAMERA_DEBOUNCE_MS = 250
const STATUS_COLORS = {
  open:        "#dc2626", // red-600
  in_progress: "#f59e0b", // amber-500
  resolved:    "#16a34a", // green-600
  unknown:     "#9ca3af"  // gray-400
}
const ORPHAN_COLOR = "#9ca3af"

// § 3.5 Tier 7 Step 80 — discipline-based palette. Falls back to the
// status palette when the pin has no `discipline` hint. Keeps the
// scene intelligible on a federated NWD where 30+ pins on a level
// would otherwise render as a uniform red cluster.
const DISCIPLINE_COLORS = {
  architecture: "#2563eb", // blue-600
  structural:   "#7c3aed", // violet-600
  mechanical:   "#0891b2", // cyan-600
  electrical:   "#ea580c", // orange-600
  plumbing:     "#0d9488", // teal-600
  civil:        "#65a30d", // lime-600
  landscape:    "#16a34a", // green-600
  other:        "#475569"  // slate-600
}

export default class extends Controller {
  static values = {
    pinsUrl: String
  }

  connect() {
    this._pins = []
    this._sprites = []
    this._dbMap = null
    this._cameraTimer = null
    this._lastModelId = null
    // Texture cache lives on the controller, not per-render — sprites
    // of the same color share a texture, so disposing per-sprite
    // (`_tearDownSprites`) would double-dispose. Texture lifecycle
    // is bound to the controller's `disconnect`.
    this._textureCache = {}
    // Expose ourselves on the shared root so `forge_viewer` can poke
    // `reloadAfterViewSwap` directly when it loads a new view (vs.
    // making us poll on every CAMERA_CHANGE_EVENT to detect swaps).
    this.element._bimPinOverlay = this
    // The viewer mounts asynchronously — wait for it to expose itself
    // on the shared root element. We use a short polling loop rather
    // than a custom event so the contract stays simple (no extra
    // events to maintain on the forge_viewer side).
    this._readyTimer = setInterval(() => this._tryWireToViewer(), PIN_FETCH_RETRY_MS)
  }

  disconnect() {
    if (this._readyTimer) clearInterval(this._readyTimer)
    if (this._cameraTimer) clearTimeout(this._cameraTimer)
    this._tearDownSprites()
    this._disposeTextureCache()
    this._unwireViewerEvents()
    this.element._bimPinOverlay = null
  }

  _disposeTextureCache() {
    Object.values(this._textureCache || {}).forEach(t => {
      try { t.dispose() } catch (_) {}
    })
    this._textureCache = {}
  }

  _tryWireToViewer() {
    const viewer = this.element._forgeViewer
    if (!viewer) return
    const model = this.element._forgeViewerModel
    if (!model) return
    if (this._wired && this._lastModelId === model.id) return
    this._wired = true
    this._viewer = viewer
    this._model  = model
    this._lastModelId = model.id
    this._unwireViewerEvents()
    this._wireViewerEvents()
    this._fetchPinsAndRender()
    if (this._readyTimer) {
      clearInterval(this._readyTimer)
      this._readyTimer = null
    }
  }

  _wireViewerEvents() {
    const Viewing = window.Autodesk && window.Autodesk.Viewing
    if (!Viewing) return
    this._cameraHandler = () => this._onCameraChanged()
    this._viewer.addEventListener(Viewing.CAMERA_CHANGE_EVENT, this._cameraHandler)
  }

  _unwireViewerEvents() {
    const Viewing = window.Autodesk && window.Autodesk.Viewing
    if (!Viewing || !this._viewer || !this._cameraHandler) return
    try { this._viewer.removeEventListener(Viewing.CAMERA_CHANGE_EVENT, this._cameraHandler) } catch (_) {}
    this._cameraHandler = null
  }

  // Public — the forge_viewer controller calls this after a `loadView`
  // to refresh the overlay against the new dbId table. Same external
  // contract as the Tier-3 viewpoint round-trip — externalIds are
  // re-resolved per swap.
  reloadAfterViewSwap() {
    this._tearDownSprites()
    this._dbMap = null
    if (this._wired) {
      this._lastModelId = null
      this._wired = false
    }
    this._tryWireToViewer()
  }

  async _fetchPinsAndRender() {
    if (!this.hasPinsUrlValue || this.pinsUrlValue.length === 0) return
    try {
      const res = await fetch(this.pinsUrlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) return
      const json = await res.json()
      this._pins = Array.isArray(json.pins) ? json.pins : []
    } catch (_) {
      return
    }
    await this._resolveDbMap()
    this._renderSprites()
  }

  async _resolveDbMap() {
    if (this._dbMap) return this._dbMap
    return new Promise(resolve => {
      try {
        this._model.getExternalIdMapping((extToDb) => {
          this._dbMap = extToDb || {}
          resolve(this._dbMap)
        }, () => { this._dbMap = {}; resolve(this._dbMap) })
      } catch (_) {
        this._dbMap = {}
        resolve(this._dbMap)
      }
    })
  }

  _renderSprites() {
    this._tearDownSprites()
    if (!window.THREE || !this._viewer) return

    const scene = this._viewer.impl && this._viewer.impl.scene_3d
    if (!scene) return

    this._pins.forEach(pin => {
      const orphan = !this._isPinAnchorResolvable(pin)
      const status = orphan ? "unknown" : (pin.display_status || "open")
      const color  = orphan ? ORPHAN_COLOR : (
        // Pins that explicitly carry a discipline (e.g. linked-document
        // models) use the discipline palette so colors line up with
        // the Disciplines panel; everything else falls back to the
        // status palette so reviewers see at-a-glance progress.
        (pin.discipline && DISCIPLINE_COLORS[pin.discipline]) ||
        STATUS_COLORS[status] || STATUS_COLORS.open
      )
      const tex    = this._textureFor(color)

      const material = new window.THREE.SpriteMaterial({
        map: tex,
        depthTest: false,
        transparent: true
      })
      const sprite = new window.THREE.Sprite(material)
      // 3D-units sprite scale — viewer scenes are typically in feet
      // or meters; 0.6 m / 2 ft reads cleanly without overwhelming
      // small elements.
      sprite.scale.set(0.6, 0.6, 0.6)
      const pos = pin.position || {}
      const x = Number(pos.x) || 0
      const y = Number(pos.y) || 0
      const z = Number(pos.z) || 0
      sprite.position.set(x, y, z)
      sprite.userData = {
        pinId:        pin.id,
        punchlistId:  pin.punchlist_id,
        viewpointId:  pin.viewpoint_id,
        slideOverUrl: pin.slide_over_url,
        title:        pin.pretty_title || pin.title || "",
        orphan:       orphan,
        externalIds:  pin.element_external_ids || []
      }
      scene.add(sprite)
      this._sprites.push(sprite)
    })

    // Pin clicks are detected via raycaster on canvas mousedown. We
    // intentionally bind on the viewer's canvas — not the document —
    // so a click outside the canvas (e.g. on the pins-list rail)
    // doesn't false-positive trigger the overlay.
    this._wireSpriteClicks()
    this._viewer.impl.invalidate(true, true, true)
  }

  _wireSpriteClicks() {
    if (this._clickHandlerWired) return
    const canvas = this._viewer.canvas
    if (!canvas) return
    this._clickHandler = (e) => this._handleCanvasClick(e)
    canvas.addEventListener("mousedown", this._clickHandler)
    this._clickHandlerWired = true
  }

  _handleCanvasClick(event) {
    if (event.button !== 0) return
    if (!window.THREE || this._sprites.length === 0) return
    const canvas = this._viewer.canvas
    const rect = canvas.getBoundingClientRect()
    const ndc = new window.THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    )
    const raycaster = new window.THREE.Raycaster()
    const camera = this._viewer.impl && this._viewer.impl.camera
    if (!camera) return
    raycaster.setFromCamera(ndc, camera)
    const hits = raycaster.intersectObjects(this._sprites, false)
    if (hits.length === 0) return
    // Sprites are AABBs from THREE's perspective, so a raycast hits
    // the bounding square — including the corners outside the visible
    // disk drawn at radius (size/2 - 4) on a `size` canvas. Ignore
    // hits whose UV is outside that radius so the ~4px outline halo
    // around the pin doesn't swallow element-pick clicks.
    const uv = hits[0].uv
    if (uv) {
      const dx = uv.x - 0.5
      const dy = uv.y - 0.5
      const PIN_RADIUS_UV = (32 - 4) / 64 // matches `_textureFor` geometry
      if (Math.hypot(dx, dy) > PIN_RADIUS_UV) return
    }
    event.stopPropagation()
    event.preventDefault()
    const sprite = hits[0].object
    this._handlePinClick(sprite.userData)
  }

  _handlePinClick(meta) {
    if (!meta || !meta.punchlistId) return
    this._track("Pin Clicked", {
      punchlist_id: meta.punchlistId,
      orphan: Boolean(meta.orphan)
    })

    // Restore the viewpoint first (the visual context the user is
    // about to read about) — then surface the slide-over so the
    // animation lands on a settled camera.
    if (meta.viewpointId) this._restoreViewpoint(meta.viewpointId)

    this._openSlideOver(meta)
  }

  async _restoreViewpoint(viewpointId) {
    const url = this.element.dataset.forgeViewerViewpointsUrlValue ||
                this.element.getAttribute("data-forge-viewer-viewpoints-url-value")
    if (!url) return
    // Delegate to the forge_viewer controller — it owns the dbId
    // mapping + restoreState event-wait. Keep the boundary clean: this
    // controller signals intent, that controller owns the SDK call.
    const ev = new CustomEvent("forge:restore-viewpoint", { detail: { viewpointId }, bubbles: true })
    this.element.dispatchEvent(ev)
    this._track("Viewpoint Restored From Pin", { viewpoint_id: viewpointId })
  }

  _openSlideOver(meta) {
    // The Action Items page (`/projects/:id/action_items`) uses the
    // `action-items` Stimulus controller's slide-over. The viewer
    // page doesn't mount that DOM, so we open the slide-over in a
    // new tab — the user keeps the viewer + the just-restored
    // viewpoint, and reads/replies in the new tab. A future Tier 7
    // polish could mount an inline slide-over on the viewer page;
    // for now the new-tab handoff is the honest contract.
    //
    // Popup-blocker fallback used to navigate same-tab, but that
    // threw away the camera animation we just queued. Better to do
    // nothing visible and surface a console warning the user can
    // recover from — they can right-click the rail-panel row to
    // open the slide-over directly.
    if (!meta.slideOverUrl) return
    this._track("Pin Slide Over Opened", {
      punchlist_id: meta.punchlistId,
      orphan: Boolean(meta.orphan)
    })
    const opened = window.open(meta.slideOverUrl, "_blank", "noopener")
    if (!opened) {
      console.warn("[bim-pin-overlay] popup blocked — open the action item from the right-rail panel instead.")
    }
  }

  _onCameraChanged() {
    if (this._cameraTimer) clearTimeout(this._cameraTimer)
    this._cameraTimer = setTimeout(() => this._cluster(), CAMERA_DEBOUNCE_MS)
  }

  // Simple distance-based clustering at screen space. Sprites within
  // ~24 px of each other collapse into a single "halo" sprite (we
  // hide the others). On zoom-in the halo expands back. Cheap O(n²)
  // — we expect <200 pins per model in practice; if a customer pushes
  // past that we'll swap in a quad-tree.
  _cluster() {
    if (this._sprites.length === 0) return
    const camera = this._viewer.impl && this._viewer.impl.camera
    const canvas = this._viewer.canvas
    if (!camera || !canvas) return
    const rect = canvas.getBoundingClientRect()
    const W = rect.width
    const H = rect.height
    const screen = this._sprites.map(s => {
      const v = s.position.clone().project(camera)
      return {
        sprite: s,
        x: (v.x * 0.5 + 0.5) * W,
        y: (-v.y * 0.5 + 0.5) * H,
        z: v.z
      }
    })
    const visible = new Array(screen.length).fill(true)
    const THRESHOLD_SQ = 24 * 24
    for (let i = 0; i < screen.length; i++) {
      if (!visible[i]) continue
      for (let j = i + 1; j < screen.length; j++) {
        if (!visible[j]) continue
        const dx = screen[i].x - screen[j].x
        const dy = screen[i].y - screen[j].y
        if (dx * dx + dy * dy < THRESHOLD_SQ) visible[j] = false
      }
    }
    let dirty = false
    screen.forEach((p, i) => {
      const want = visible[i] && p.z >= -1 && p.z <= 1
      if (p.sprite.visible !== want) {
        p.sprite.visible = want
        dirty = true
      }
    })
    if (dirty) this._viewer.impl.invalidate(false, false, true)
  }

  _isPinAnchorResolvable(pin) {
    if (!this._dbMap) return true // nothing to compare against yet
    const ids = pin.element_external_ids || []
    if (ids.length === 0) return true
    return ids.some(extId => this._dbMap[extId] != null)
  }

  _textureFor(color) {
    if (this._textureCache[color]) return this._textureCache[color]
    const size = 64
    const canvas = document.createElement("canvas")
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext("2d")
    ctx.beginPath()
    ctx.arc(size / 2, size / 2, size / 2 - 4, 0, Math.PI * 2)
    ctx.fillStyle = color
    ctx.fill()
    ctx.lineWidth = 4
    ctx.strokeStyle = "#ffffff"
    ctx.stroke()
    const tex = new window.THREE.Texture(canvas)
    tex.needsUpdate = true
    this._textureCache[color] = tex
    return tex
  }

  _tearDownSprites() {
    if (this._sprites.length === 0) return
    const scene = this._viewer && this._viewer.impl && this._viewer.impl.scene_3d
    if (scene) {
      this._sprites.forEach(s => {
        scene.remove(s)
        // Dispose the per-sprite material; the texture lives in
        // `this._textureCache` and is shared across same-color
        // sprites + reused across renders, so disposing it here
        // would double-dispose. Texture lifecycle ends in
        // `_disposeTextureCache` on `disconnect`.
        if (s.material) s.material.dispose()
      })
    }
    this._sprites = []
    if (this._clickHandler && this._viewer && this._viewer.canvas) {
      this._viewer.canvas.removeEventListener("mousedown", this._clickHandler)
    }
    this._clickHandler = null
    this._clickHandlerWired = false
  }

  _track(name, props) {
    try {
      if (window.mixpanel && typeof window.mixpanel.track === "function") {
        window.mixpanel.track(name, Object.assign({ source: "bim_pin_overlay" }, props || {}))
      }
    } catch (_) {}
  }
}
